import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Grid,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { useSnackbar } from "notistack";

import TableComponent from "../../components/table";
import DialogComponent from "../../components/Dialog";
import ButtonWithLoading from "../../components/ButtonWithLoading";
import { Get } from "../../actions";
import { Get_CronJobRuns_URL } from "../../constants/apiUrls";
import { getTranslation } from "../../heplers/translationHelper";
import { format } from "../../heplers/format";

const useStyles = makeStyles(() => ({
  detail: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontFamily: "monospace",
    fontSize: "13px",
    margin: 0,
  },
}));

const statusStyle = (statusName) => {
  switch (statusName) {
    case "Succeeded":
      return { backgroundColor: "#e6f4ea", color: "#1b5e20" };
    case "Failed":
      return { backgroundColor: "#fdecea", color: "#b71c1c" };
    case "Skipped":
      return { backgroundColor: "#fff4e5", color: "#8a5300" };
    default:
      return { backgroundColor: "#eeeeee", color: "#424242" };
  }
};

/**
 * Every trigger of this job, as data rather than prose: one row per customer per run, with the
 * status code and body that came back.
 */
const CronJobRuns = ({ cronJob, customerId, onChangeCustomer, customers }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [runs, setRuns] = useState([]);
  const [status, setStatus] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRun, setSelectedRun] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    getRuns();
    // eslint-disable-next-line
  }, [cronJob?.id, customerId, status, page, rowsPerPage]);

  const getRuns = () => {
    setIsLoading(true);
    const params = {
      id: cronJob.id,
      pageNumber: page,
      pageSize: rowsPerPage,
    };
    if (customerId) params.customerId = customerId;
    if (status !== "") params.status = status;

    Get(
      params,
      Get_CronJobRuns_URL,
      null,
      (resp) => {
        setRuns(resp.data);
        setIsLoading(false);
      },
      (error) => {
        enqueueSnackbar(error?.data ? error.data : "internal server error", {
          variant: "error",
        });
        setIsLoading(false);
      }
    );
  };

  const columns = [
    {
      id: "startedAt",
      numeric: false,
      disablePadding: true,
      label: getTranslation("Started", "Démarré", "Gestartet"),
      component: ({ row }) => (
        <Typography noWrap>{format("dateTime", row?.startedAt)}</Typography>
      ),
    },
    {
      id: "statusName",
      numeric: false,
      disablePadding: true,
      label: getTranslation("Status", "Statut", "Status"),
      component: ({ row }) => (
        <Chip
          size="small"
          label={row?.statusName}
          style={statusStyle(row?.statusName)}
        />
      ),
    },
    {
      id: "customerId",
      numeric: false,
      disablePadding: true,
      label: getTranslation("Customer", "Client", "Kunde"),
      component: ({ row }) => (
        <Box>
          <Typography noWrap>{row?.organizationName || "-"}</Typography>
          <Typography variant="caption" color="textSecondary" noWrap>
            {row?.customerId}
          </Typography>
        </Box>
      ),
    },
    {
      id: "triggeredBy",
      numeric: false,
      disablePadding: true,
      label: getTranslation("Trigger", "Déclencheur", "Auslöser"),
      component: ({ row }) => <Typography noWrap>{row?.triggeredBy}</Typography>,
    },
    {
      id: "message",
      numeric: false,
      disablePadding: false,
      label: getTranslation("Result", "Résultat", "Ergebnis"),
      component: ({ row }) => (
        <Tooltip title={row?.message || ""}>
          <Typography>{row?.message}</Typography>
        </Tooltip>
      ),
    },
    {
      id: "durationMs",
      numeric: true,
      disablePadding: true,
      label: getTranslation("Duration", "Durée", "Dauer"),
      component: ({ row }) => (
        <Typography noWrap>
          {row?.durationMs || row?.durationMs === 0 ? `${row.durationMs} ms` : "-"}
        </Typography>
      ),
    },
    {
      id: "actions",
      numeric: true,
      disablePadding: false,
      align: "right",
      label: getTranslation("Details", "Détails", "Details"),
    },
  ];

  return (
    <Box>
      <Box mb={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              size="small"
              variant="outlined"
              label={getTranslation("Customer", "Client", "Kunde")}
              value={customerId || ""}
              onChange={(e) => {
                setPage(0);
                onChangeCustomer(e.target.value);
              }}
            >
              <MenuItem value="">
                {getTranslation("All customers", "Tous les clients", "Alle Kunden")}
              </MenuItem>
              {customers?.map((item) => (
                <MenuItem key={item.customerId} value={item.customerId}>
                  {item.organizationName
                    ? `${item.organizationName} (${item.customerId})`
                    : item.customerId}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              variant="outlined"
              label={getTranslation("Status", "Statut", "Status")}
              value={status}
              onChange={(e) => {
                setPage(0);
                setStatus(e.target.value);
              }}
            >
              <MenuItem value="">
                {getTranslation("All", "Tous", "Alle")}
              </MenuItem>
              <MenuItem value={1}>
                {getTranslation("Succeeded", "Réussi", "Erfolgreich")}
              </MenuItem>
              <MenuItem value={2}>
                {getTranslation("Failed", "Échoué", "Fehlgeschlagen")}
              </MenuItem>
              <MenuItem value={3}>
                {getTranslation("Skipped", "Ignoré", "Übersprungen")}
              </MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Box>

      <TableComponent
        dense
        loading={isLoading}
        colums={columns}
        count={runs?.totalCount}
        rows={runs?.data}
        page={page}
        rowsPerPage={rowsPerPage}
        handleChangePage={(event, newPage) => setPage(newPage)}
        handleChangeRowsPerPage={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        actions={[
          {
            component: (rowData) => (
              <ButtonWithLoading
                label={getTranslation("View", "Voir", "Ansehen")}
                size="small"
                variant="contained"
                color="primary"
                onClick={() => setSelectedRun(rowData)}
              />
            ),
          },
        ]}
      />

      <DialogComponent
        open={Boolean(selectedRun)}
        onClose={() => setSelectedRun(null)}
        fullWidth
        maxWidth="md"
        title={getTranslation("Run details", "Détails de l'exécution", "Lauf-Details")}
      >
        {selectedRun ? (
          <Box p={2}>
            <Typography variant="subtitle2">
              {format("dateTime", selectedRun.startedAt)} &nbsp;|&nbsp;{" "}
              {selectedRun.statusName} &nbsp;|&nbsp; {selectedRun.triggeredBy}
            </Typography>
            <Box mt={1}>
              <Typography variant="body2">
                <b>{getTranslation("Customer", "Client", "Kunde")}:</b>{" "}
                {selectedRun.organizationName
                  ? `${selectedRun.organizationName} (${selectedRun.customerId})`
                  : selectedRun.customerId}
              </Typography>
              <Typography variant="body2">
                <b>{getTranslation("Route", "Route", "Route")}:</b>{" "}
                {selectedRun.route}
              </Typography>
              <Typography variant="body2">
                <b>{getTranslation("HTTP status", "Statut HTTP", "HTTP-Status")}:</b>{" "}
                {selectedRun.statusCode || "-"}
              </Typography>
              <Typography variant="body2">
                <b>{getTranslation("Attempts", "Tentatives", "Versuche")}:</b>{" "}
                {selectedRun.attempts}
              </Typography>
              <Typography variant="body2">
                <b>{getTranslation("Duration", "Durée", "Dauer")}:</b>{" "}
                {selectedRun.durationMs || selectedRun.durationMs === 0
                  ? `${selectedRun.durationMs} ms`
                  : "-"}
              </Typography>
            </Box>
            <Box mt={2}>
              <Typography variant="body2">
                <b>{getTranslation("Result", "Résultat", "Ergebnis")}</b>
              </Typography>
              <Typography variant="body2">{selectedRun.message}</Typography>
            </Box>
            {selectedRun.detail ? (
              <Box mt={2}>
                <Typography variant="body2">
                  <b>
                    {getTranslation(
                      "Response / exception",
                      "Réponse / exception",
                      "Antwort / Ausnahme"
                    )}
                  </b>
                </Typography>
                <pre className={classes.detail}>{selectedRun.detail}</pre>
              </Box>
            ) : null}
          </Box>
        ) : null}
      </DialogComponent>
    </Box>
  );
};

export default CronJobRuns;
